// Diagnósticos · Detalle de proyecto. Reutiliza los endpoints existentes de
// apps/diagnostics: participantes, invitaciones (con vista previa = dry_run),
// análisis, reporte agregado, resultado del dueño (borrador → enviar) y seguimiento.
import { useCallback, useEffect, useState } from 'react';
import {
  ArrowLeft, BarChart3, Copy, Eye, FileText, Mail, Play, RefreshCw, Send, Settings2, Trash2, UserPlus, Repeat,
} from 'lucide-react';

import { Modal } from '@/components/ui/Modal';
import * as api from '../api';
import type {
  DiagParticipant, DiagProgram, DiagProject, EffectiveView, InvitationPreview, ParticipantDetail, ParticipantResult,
  ProjectReport,
} from '../types';
import {
  Btn, Chip, DataView, Empty, ErrorBox, Field, Panel, ROLE_LABEL, Spinner, STATUS_LABEL, TIPO_LABEL, fmtDate,
  inputCls, statusTone,
} from './ui';

const SOURCE_LABEL: Record<string, string> = {
  platform: 'plataforma', org: 'org', program: 'programa', project: 'proyecto', gift_config: 'config legada',
};

function InviteDialog({ project, ids, onClose, onDone }: { project: DiagProject; ids: string[] | null; onClose: () => void; onDone: (msg: string) => void }) {
  const [preview, setPreview] = useState<InvitationPreview[] | null>(null);
  const [sender, setSender] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    api.previewInvitations(project.id, ids)
      .then((r) => { setPreview(r.emails); setSender(r.sender_org); })
      .catch((e) => setError((e as Error).message));
  }, [project.id, ids]);
  const sendAll = async () => {
    if (!window.confirm(`Se enviarán ${preview?.length ?? 0} correo(s) reales desde el SMTP de «${sender}». ¿Continuar?`)) return;
    setBusy(true);
    setError('');
    try {
      const r = await api.sendInvitations(project.id, ids);
      const failed = r.results.filter((x) => !x.sent);
      onDone(`Enviadas ${r.sent}/${r.count}.${failed.length ? ` Fallaron ${failed.length}: ${failed[0].error}` : ''}`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open onClose={onClose} title="Invitaciones · vista previa" size="xl"
      footer={<div className="flex justify-end gap-2"><Btn onClick={onClose}>Cerrar</Btn><Btn kind="primary" busy={busy} disabled={!preview?.length} onClick={sendAll}><Send className="h-3.5 w-3.5" /> Enviar</Btn></div>}>
      <div className="space-y-3">
        <ErrorBox error={error} />
        {!preview && !error && <Spinner />}
        {preview && preview.length === 0 && <Empty>No hay participantes pendientes de invitar (los de prueba se omiten).</Empty>}
        {preview && preview.length > 0 && <p className="text-xs text-muted-foreground">Remitente: SMTP de la org «{sender}». Nada sale hasta que pulses Enviar.</p>}
        {preview?.map((m) => (
          <div key={m.participant_id} className="rounded-lg border border-border p-3">
            <div className="text-xs text-muted-foreground">Para: {m.to}</div>
            <div className="text-sm font-semibold text-foreground">{m.subject}</div>
            <pre className="mt-2 whitespace-pre-wrap font-sans text-sm text-foreground">{m.body}</pre>
          </div>
        ))}
      </div>
    </Modal>
  );
}

function ParticipantDialog({ projectId, pid, onClose }: { projectId: string; pid: string; onClose: () => void }) {
  const [d, setD] = useState<ParticipantDetail | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    api.getParticipant(projectId, pid).then(setD).catch((e) => setError((e as Error).message));
  }, [projectId, pid]);
  return (
    <Modal open onClose={onClose} title={d ? `${d.name} · ${ROLE_LABEL[d.role] ?? ''}` : 'Participante'} size="xl">
      <ErrorBox error={error} />
      {!d && !error && <Spinner />}
      {d && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2"><Chip tone={statusTone(d.status)}>{STATUS_LABEL[d.status] ?? d.status}</Chip>{d.last_error && <Chip tone="bad">{d.last_error}</Chip>}</div>
          <Panel title="Análisis">{d.analysis ? <DataView data={d.analysis.data} /> : <p className="text-sm text-muted-foreground">Aún sin análisis.</p>}</Panel>
          <Panel title="Conversación">
            {d.transcript_error && <ErrorBox error={d.transcript_error} />}
            {d.transcript?.length ? (
              <div className="max-h-96 space-y-2 overflow-y-auto">
                {d.transcript.map((t, i) => (
                  <div key={i} className={`rounded-lg px-3 py-2 text-sm ${t.role === 'user' ? 'bg-primary/10 text-foreground' : 'bg-muted text-foreground'}`}>
                    <div className="text-[10px] uppercase text-muted-foreground">{t.role === 'user' ? d.name : 'Entrevistador'}</div>
                    <div className="whitespace-pre-wrap">{t.content}</div>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground">Sin conversación todavía.</p>}
          </Panel>
        </div>
      )}
    </Modal>
  );
}

function ResultDialog({ p, onClose }: { p: DiagParticipant; onClose: () => void }) {
  const [r, setR] = useState<ParticipantResult | null>(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const load = useCallback(() => api.getResult(p.id).then(setR).catch((e) => setError((e as Error).message)), [p.id]);
  useEffect(() => { void load(); }, [load]);
  const act = async (kind: 'draft' | 'release') => {
    if (kind === 'release' && !window.confirm(`Se publicará el resultado y se enviará un correo real a ${p.email}. ¿Continuar?`)) return;
    setBusy(kind);
    setError('');
    try {
      setR(kind === 'draft' ? await api.draftResult(p.id) : await api.releaseResult(p.id));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy('');
    }
  };
  return (
    <Modal open onClose={onClose} title={`Resultado para ${p.name}`} size="xl"
      footer={<div className="flex flex-wrap justify-end gap-2">
        <Btn onClick={() => act('draft')} busy={busy === 'draft'} disabled={p.status !== 'analizado'}><RefreshCw className="h-3.5 w-3.5" /> Generar borrador</Btn>
        <Btn kind="primary" onClick={() => act('release')} busy={busy === 'release'} disabled={!r?.result_draft}><Send className="h-3.5 w-3.5" /> Enviar resultado</Btn>
      </div>}>
      <div className="space-y-3">
        <ErrorBox error={error} />
        {!r && !error && <Spinner />}
        {r && (
          <>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Chip tone={statusTone(r.result_status)}>{r.result_status === 'released' ? 'Enviado' : r.result_status === 'draft' ? 'Borrador' : 'Sin resultado'}</Chip>
              {r.released_at && <span className="text-muted-foreground">Enviado {fmtDate(r.released_at)}</span>}
              {r.pending_changes && <Chip tone="warn">Borrador con cambios sin enviar</Chip>}
              {r.result_status === 'released' && <a className="text-primary underline" href={r.result_link} target="_blank" rel="noreferrer">Ver como el participante</a>}
            </div>
            {r.result_draft ? <Panel title="Borrador"><DataView data={r.result_draft} /></Panel>
              : <p className="text-sm text-muted-foreground">Sin borrador. Se genera con el análisis del participante (y el agregado del equipo si existe).</p>}
          </>
        )}
      </div>
    </Modal>
  );
}

export function ProjectDetail({ projectId, onBack, programs }: { projectId: string; onBack: () => void; programs: DiagProgram[] }) {
  const [project, setProject] = useState<DiagProject | null>(null);
  const [report, setReport] = useState<ProjectReport | null>(null);
  const [config, setConfig] = useState<EffectiveView | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [raw, setRaw] = useState('');
  const [isTest, setIsTest] = useState(false);
  const [busy, setBusy] = useState('');
  const [inviteIds, setInviteIds] = useState<string[] | null | undefined>(undefined);
  const [viewing, setViewing] = useState<string>('');
  const [resultFor, setResultFor] = useState<DiagParticipant | null>(null);
  const [showConfig, setShowConfig] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const p = await api.getProject(projectId);
      setProject(p);
      if (p.last_report) setReport(await api.getReport(projectId).catch(() => null));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [projectId]);
  useEffect(() => { void load(); }, [load]);

  const run = async (key: string, fn: () => Promise<unknown>, ok?: string) => {
    setBusy(key);
    setError('');
    try {
      await fn();
      if (ok) setNotice(ok);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy('');
    }
  };

  if (!project) return <div className="p-6">{error ? <ErrorBox error={error} /> : <Spinner />}</div>;
  const parts = project.participants ?? [];
  const owners = new Map(parts.map((x) => [x.id, x.name]));
  const rep = report?.status === 'listo' ? report : report?.vigente ?? null;

  return (
    <div className="space-y-4 p-4 md:p-6">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Proyectos</button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{project.name}</h2>
          <p className="text-sm text-muted-foreground">{project.client.name}{project.objective ? ` · ${project.objective}` : ''}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Chip>{TIPO_LABEL[project.tipo] ?? project.tipo}</Chip>
            {project.program && <Chip tone="primary">{project.program.name}</Chip>}
            {project.enfoque && <Chip>{project.enfoque}</Chip>}
            <Chip>Entrevista: {project.interview_org}</Chip>
            <Chip>Remitente: {project.sender_org}</Chip>
            {project.auto_result && <Chip tone={project.auto_result.state === 'failed' ? 'bad' : 'muted'}>Envío automático: {project.auto_result.state || 'en espera'}</Chip>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select className={`${inputCls} w-auto`} value={project.status} aria-label="Estado"
            onChange={(e) => run('status', () => api.updateProject(project.id, { status: e.target.value }))}>
            <option value="borrador">Borrador</option><option value="activo">Activo</option><option value="cerrado">Cerrado</option>
          </select>
          <select className={`${inputCls} w-auto`} value={project.program_id ?? ''} aria-label="Programa"
            onChange={(e) => run('program', () => api.updateProject(project.id, { program_id: e.target.value || null }))}>
            <option value="">(sin programa)</option>
            {programs.filter((p) => p.status !== 'archivado' || p.id === project.program_id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <Btn onClick={load}><RefreshCw className="h-3.5 w-3.5" /></Btn>
        </div>
      </div>
      <ErrorBox error={error} />
      {notice && <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-foreground">{notice}</div>}

      <Panel title={`Participantes (${parts.length})`} actions={<>
        <Btn onClick={() => setInviteIds(null)}><Mail className="h-3.5 w-3.5" /> Invitar pendientes</Btn>
      </>}>
        {parts.length === 0 ? <Empty>Sin participantes. Pega la lista abajo.</Empty> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr><th className="py-1 pr-3 font-medium">Persona</th><th className="py-1 pr-3 font-medium">Rol</th><th className="py-1 pr-3 font-medium">Estado</th><th className="py-1 pr-3 font-medium">Avance</th><th className="py-1 font-medium">Acciones</th></tr>
              </thead>
              <tbody>
                {parts.map((x) => (
                  <tr key={x.id} className="border-t border-border align-top">
                    <td className="py-2 pr-3">
                      <div className="font-medium text-foreground">{x.name}{x.is_test && <span className="ml-1 text-[10px] text-muted-foreground">(prueba)</span>}</div>
                      <div className="text-xs text-muted-foreground">{x.email}</div>
                      {x.invited_by_id && <div className="text-[11px] text-muted-foreground">Invitado por {owners.get(x.invited_by_id) ?? '—'}</div>}
                    </td>
                    <td className="py-2 pr-3"><Chip tone={x.role === 'owner' ? 'primary' : 'muted'}>{ROLE_LABEL[x.role] ?? x.role}</Chip></td>
                    <td className="py-2 pr-3">
                      <Chip tone={statusTone(x.status)}>{STATUS_LABEL[x.status] ?? x.status}</Chip>
                      {x.result_status === 'released' && <div className="mt-1"><Chip tone="ok">Resultado enviado</Chip></div>}
                      {x.last_error && <div className="mt-1 max-w-xs text-[11px] text-destructive">{x.last_error}</div>}
                    </td>
                    <td className="py-2 pr-3 text-[11px] text-muted-foreground">
                      <div>Invitado: {fmtDate(x.invited_at)}</div><div>Inició: {fmtDate(x.started_at)}</div>
                      <div>Terminó: {fmtDate(x.completed_at)}</div><div>Analizado: {fmtDate(x.analyzed_at)}</div>
                    </td>
                    <td className="py-2">
                      <div className="flex flex-wrap gap-1">
                        <Btn onClick={() => { void navigator.clipboard?.writeText(x.link); setNotice(`Enlace de ${x.name} copiado.`); }}><Copy className="h-3.5 w-3.5" /></Btn>
                        <Btn onClick={() => setViewing(x.id)}><Eye className="h-3.5 w-3.5" /></Btn>
                        <Btn onClick={() => setInviteIds([x.id])}><Mail className="h-3.5 w-3.5" /></Btn>
                        <Btn busy={busy === `a-${x.id}`} disabled={!x.started_at && !x.completed_at}
                          onClick={() => run(`a-${x.id}`, () => api.analyzeParticipant(project.id, x.id), `Análisis de ${x.name} en curso.`)}><Play className="h-3.5 w-3.5" /></Btn>
                        <Btn onClick={() => setResultFor(x)}><FileText className="h-3.5 w-3.5" /></Btn>
                        <Btn kind="danger" onClick={() => { if (window.confirm(`¿Quitar a ${x.name}? Su enlace deja de funcionar.`)) void run(`d-${x.id}`, () => api.removeParticipant(project.id, x.id)); }}><Trash2 className="h-3.5 w-3.5" /></Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-4 space-y-2 border-t border-border pt-3">
          <Field label="Agregar participantes" hint="Una persona por línea: «Nombre    correo». Se deduplica por correo.">
            <textarea className={inputCls} rows={3} value={raw} onChange={(e) => setRaw(e.target.value)} placeholder={'Ana López   ana@empresa.mx\nLuis Gómez  luis@empresa.mx'} />
          </Field>
          <div className="flex items-center gap-3">
            <Btn kind="primary" busy={busy === 'add'} disabled={!raw.trim()} onClick={() => run('add', async () => {
              const r = await api.addParticipants(project.id, raw, isTest);
              setNotice(`Agregados ${r.created.length}. Duplicados ${r.duplicates.length}. Inválidos ${r.invalid.length}.`);
              setRaw('');
            })}><UserPlus className="h-3.5 w-3.5" /> Agregar</Btn>
            <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><input type="checkbox" checked={isTest} onChange={() => setIsTest((v) => !v)} /> De prueba</label>
          </div>
        </div>
      </Panel>

      <Panel title="Reporte agregado" actions={<>
        <Btn busy={busy === 'report'} onClick={() => run('report', () => api.generateReport(project.id), 'Reporte en generación (tarda 3–6 min).')}><BarChart3 className="h-3.5 w-3.5" /> Generar reporte</Btn>
        <Btn kind="secondary" busy={busy === 'fu'} onClick={() => run('fu', async () => {
          const p = await api.followUp(project.id);
          setNotice(`Seguimiento creado: «${p.name}».`);
        })}><Repeat className="h-3.5 w-3.5" /> Crear seguimiento</Btn>
      </>}>
        {report && report.status !== 'listo' && <div className="mb-2"><Chip tone={statusTone(report.status === 'error' ? 'error' : 'analizando')}>{report.status === 'error' ? `Error: ${report.error}` : 'Generando…'}</Chip></div>}
        {rep ? <DataView data={rep.data} /> : <p className="text-sm text-muted-foreground">Aún no hay reporte. Se arma con los participantes analizados.</p>}
      </Panel>

      <Panel title="Configuración efectiva" actions={<Btn onClick={async () => {
        if (!showConfig && !config) setConfig(await api.projectConfig(project.id).catch((e) => { setError((e as Error).message); return null; }));
        setShowConfig((v) => !v);
      }}><Settings2 className="h-3.5 w-3.5" /> {showConfig ? 'Ocultar' : 'Ver'}</Btn>}>
        {!showConfig ? <p className="text-sm text-muted-foreground">Plataforma → org → programa → proyecto: qué rige a este proyecto y de dónde sale.</p> : config && (
          <div className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
            {Object.entries(config.values).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-2 border-b border-border py-1">
                <span className="text-muted-foreground">{k}</span>
                <span className="truncate text-right text-foreground">{Array.isArray(v) ? v.join(', ') || '—' : v === '' || v === null ? '—' : String(v)} <span className="text-muted-foreground">· {SOURCE_LABEL[config.sources[k]] ?? config.sources[k]}</span></span>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {inviteIds !== undefined && <InviteDialog project={project} ids={inviteIds} onClose={() => setInviteIds(undefined)} onDone={(m) => { setInviteIds(undefined); setNotice(m); void load(); }} />}
      {viewing && <ParticipantDialog projectId={project.id} pid={viewing} onClose={() => setViewing('')} />}
      {resultFor && <ResultDialog p={resultFor} onClose={() => { setResultFor(null); void load(); }} />}
    </div>
  );
}
